import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiX } from 'react-icons/fi';
import { colors, typography } from '../../theme/colors';

// Tiny floating card that surfaces existing audit fields (createdBy /
// postedBy / updatedBy + timestamps). Anchored to the click coordinates of
// the info icon on a CycleNode.
const fmt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const CycleAuditPopover = ({ node, anchor, userMap, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!anchor) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !anchor.contains(e.target)) {
        onClose && onClose();
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  if (!node || !anchor) return null;

  const rect = anchor.getBoundingClientRect();
  const top = rect.bottom + 6;
  const left = Math.min(rect.right - 280, window.innerWidth - 300);

  const name = (uid) => userMap?.[uid] || uid || '—';

  return createPortal(
    <Popover ref={ref} style={{ top, left: Math.max(8, left) }}>
      <Header>
        <span>{node.tranTypeCode} · {node.docNumber}</span>
        <Close onClick={onClose}><FiX size={12} /></Close>
      </Header>
      <Row><Label>Created by</Label><Value>{name(node.createdBy)}</Value></Row>
      <Row><Label>Created at</Label><Value>{fmt(node.createdAt)}</Value></Row>
      {node.postedBy && (
        <>
          <Row><Label>Posted by</Label><Value>{name(node.postedBy)}</Value></Row>
          <Row><Label>Posted at</Label><Value>{fmt(node.postedAt)}</Value></Row>
        </>
      )}
      {node.updatedBy && node.updatedAt && (
        <>
          <Row><Label>Last edited by</Label><Value>{name(node.updatedBy)}</Value></Row>
          <Row><Label>Last edited at</Label><Value>{fmt(node.updatedAt)}</Value></Row>
        </>
      )}
      <Status>Status: <strong>{node.status}</strong></Status>
    </Popover>,
    document.body
  );
};

export default CycleAuditPopover;

const Popover = styled.div`
  position: fixed;
  z-index: 9999;
  width: 280px;
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.18);
  padding: 8px 10px;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 7px;
  margin-bottom: 7px;
  border-bottom: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  font-size: 13px;
  font-weight: 700;
  color: ${colors.primary.main};
  font-family: ${typography.fontFamily.mono};
`;
const Close = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${colors.text.secondary};
  display: inline-flex;
  &:hover { color: ${colors.error.main}; }
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 12px;
`;
const Label = styled.span`
  color: ${colors.text.secondary};
  font-size: 12px;
  font-weight: 500;
`;
const Value = styled.span`
  color: ${colors.text.primary};
  font-size: 12px;
  font-weight: 600;
  text-align: right;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Status = styled.div`
  margin-top: 7px;
  padding-top: 7px;
  border-top: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  font-size: 12px;
  color: ${colors.text.secondary};
  strong { color: ${colors.text.primary}; font-weight: 700; }
`;
